// src/hooks/results/useRrDetailsFromSmoothSignal.js
import { useMemo } from "react";
import {
  detectTroughsFromSmoothSignal,
  sortedTroughTimesSec,
  buildRrPerBreathRows,
  buildRrComparisonMatrix,
  computeHeatScaleP95,
  makeHeatStyle,
} from "../../utils/rrLive/rrResultTables";

/**
 * Computes RR matrix + per-breath table from an unpacked smooth signal.
 * This is display-only. We re-detect troughs because they aren't persisted.
 */
export function useRrDetailsFromSmoothSignal({
  smoothSignal,
  overallRR,
  // knobs to align with pod behaviour
  perBreathMaxSec = 10,
  rangeMaxSec = 60,
}) {
  const troughs = useMemo(() => {
    return detectTroughsFromSmoothSignal(smoothSignal, {
      minSepSec: 0.8,
      neighborhood: 3,
      minProminence: 0,
    });
  }, [smoothSignal]);

  const troughTimes = useMemo(() => {
    return sortedTroughTimesSec(smoothSignal, troughs);
  }, [smoothSignal, troughs]);

  const rrPerBreath = useMemo(() => {
    return buildRrPerBreathRows(troughTimes, { maxSec: perBreathMaxSec });
  }, [troughTimes, perBreathMaxSec]);

  const rrMatrix = useMemo(() => {
    return buildRrComparisonMatrix(troughTimes, {
      diagMaxSec: perBreathMaxSec,
      rangeMaxSec,
    });
  }, [troughTimes, perBreathMaxSec, rangeMaxSec]);

  const heatScale = useMemo(() => {
    return computeHeatScaleP95({ rrMatrix, overallRR });
  }, [rrMatrix, overallRR]);

  const getHeatStyle = useMemo(() => {
    return makeHeatStyle({ overallRR, maxDelta: heatScale.maxDelta });
  }, [overallRR, heatScale.maxDelta]);

  return {
    rrPerBreath,
    rrMatrix,
    heatScale,
    getHeatStyle,
  };
}
